import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import UserPhoto from "./UserPhoto";

const UserMenu: React.FC = () => {
  const { user, allAccounts, logout, switchAccount, addAccount } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fermer le menu en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSwitchAccount = (accountToSwitch: typeof user) => {
    if (accountToSwitch) {
      switchAccount(accountToSwitch);
      setIsOpen(false);
    }
  };

  const handleAddAccount = async () => {
    setIsOpen(false);
    await addAccount();
  };

  const handleLogout = async (account?: typeof user) => {
    setIsOpen(false);
    if (account) {
      await logout(account);
    } else {
      await logout();
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={menuRef}>
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="hover:opacity-80 transition-opacity"
        aria-label="Menu utilisateur"
      >
        <UserPhoto
          account={user}
          size="md"
          className="border-2 border-primary-content/30"
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-base-100 rounded-lg shadow-xl border border-base-300 z-50 overflow-hidden">
          {/* Current User Section */}
          <div className="px-4 py-3 border-b border-base-300 bg-base-200">
            <div className="flex items-center gap-3">
              <UserPhoto account={user} size="lg" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">
                  {user.name || "Utilisateur"}
                </p>
                <p className="text-xs text-base-content/60 truncate">
                  {user.username}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="py-1">
            {/* Switch Account - Only if multiple accounts */}
            {allAccounts.length > 1 && (
              <div className="px-2 py-2 border-b border-base-300">
                <p className="text-xs font-semibold text-base-content/60 px-2 mb-1">
                  Changer de compte
                </p>
                {allAccounts
                  .filter((acc) => acc.homeAccountId !== user.homeAccountId)
                  .map((account) => (
                    <button
                      key={account.homeAccountId}
                      onClick={() => handleSwitchAccount(account)}
                      className="w-full flex items-center gap-2 px-2 py-2 hover:bg-base-200 rounded-md text-left transition-colors"
                    >
                      <UserPhoto account={account} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{account.name}</p>
                        <p className="text-xs text-base-content/50 truncate">
                          {account.username}
                        </p>
                      </div>
                    </button>
                  ))}
              </div>
            )}

            {/* Add Account */}
            <button
              onClick={handleAddAccount}
              className="w-full px-4 py-2 text-sm hover:bg-base-200 text-left flex items-center gap-2 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Ajouter un compte
            </button>

            {/* Logout Current Account */}
            <button
              onClick={() => handleLogout()}
              className="w-full px-4 py-2 text-sm hover:bg-base-200 text-left flex items-center gap-2 text-error transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Déconnecter ce compte
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
